import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { UserNotification } from '../../api/notificationsApi';
import { usePapeis } from '../../hooks/usePapeis';
import { notify } from '../../shared/feedback/notify';
import { alcancaPathPelosPapeis } from '../sidebar/Sidebar';
import { EVENTOS_DE_AVISO_NOVO, FonteDeAvisos, fontesDoUsuario } from './fontesDeAvisos';

export const NOTIFICACOES_POR_PAGINA = 20;
const INTERVALO_MS = 30_000;

const maisRecentePrimeiro = (a: UserNotification, b: UserNotification) =>
  new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();

/**
 * Estado das notificações do usuário logado (at-107).
 *
 * Extraído de `views/chefe_de_oficina/pages/ChefeDeOficina.tsx`, onde vivia desde sempre — era o
 * ÚNICO lugar do sistema com sino, e por isso o setor de EVENTOS não tinha onde ver os avisos de
 * comentário. Comportamento preservado: polling de 30s, paginação incremental, marcar lida,
 * marcar todas e o refetch pelo evento de janela `notification:created` (emitido por
 * `hooks/useCarStatusSocket.ts`, que vive no App).
 *
 * portal-cotacoes/at-14 (28/10/2026): junta os avisos de TODAS as fontes que a pessoa lê — SOAC,
 * Portal de Cotações e Requisições (ver `fontesDeAvisos.ts`). A lista é a união ordenada por data;
 * o contador, a soma; e "carregar mais" pede a página seguinte só de quem ainda tem o que dar.
 *
 * 🔴 Uma fonte que falha não derruba as outras, mas a faixa de erro aparece nomeando qual faltou
 * (regra da at-60): sino com aviso a menos, sem dizer, se lê como "não há nada pendente".
 *
 * ⚠️ Enquanto o dropdown está ABERTO o ciclo só atualiza o contador — refazer a lista resetaria
 * as páginas já carregadas debaixo do dedo de quem está lendo.
 */
export function useNotificacoes(aberto: boolean) {
  const papeis = usePapeis();
  const fontes = useMemo(
    () => fontesDoUsuario((path) => alcancaPathPelosPapeis(papeis, path)),
    // `papeis` é um array novo a cada render; o que importa é o conteúdo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [papeis.join(',')],
  );

  const [notificacoes, setNotificacoes] = useState<UserNotification[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [naoLidas, setNaoLidas] = useState<number>(0);
  const [carregandoMais, setCarregandoMais] = useState<boolean>(false);
  /**
   * at-60: falha de carga NÃO pode virar "nenhuma notificação". A lista vazia por erro tem a
   * mesma silhueta da lista vazia legítima, e as duas levam a conclusões opostas.
   */
  const [falhouCarga, setFalhouCarga] = useState<boolean>(false);
  /** As fontes que não responderam na última carga — a faixa de erro as nomeia. */
  const [fontesComFalha, setFontesComFalha] = useState<string[]>([]);
  /**
   * transversal/at-90: a lista já respondeu ao menos uma vez. É o que deixa o sino do topo separar
   * o que JÁ EXISTIA ao entrar (vira um resumo) do que CHEGOU depois (vira um toast por aviso).
   */
  const [carregado, setCarregado] = useState<boolean>(false);
  /** Página já carregada e total de cada fonte, para o "carregar mais". */
  const progresso = useRef<Map<FonteDeAvisos['origem'], { pagina: number; total: number }>>(new Map());

  const buscarContador = useCallback(async () => {
    const resultados = await Promise.allSettled(fontes.map((f) => f.naoLidas()));
    const ok = resultados.filter((r): r is PromiseFulfilledResult<number> => r.status === 'fulfilled');
    if (ok.length) setNaoLidas(ok.reduce((s, r) => s + r.value, 0));
    if (ok.length < resultados.length) {
      // Polling: `toastId` fixo para o aviso não empilhar um por ciclo (regra da at-60).
      notify.warning('Não foi possível atualizar o contador de notificações.', { toastId: 'sino-notificacoes' });
    }
  }, [fontes]);

  const buscar = useCallback(async () => {
    const resultados = await Promise.allSettled(
      fontes.map(async (f) => {
        const [pagina, contagem] = await Promise.all([f.pagina(1, NOTIFICACOES_POR_PAGINA), f.naoLidas()]);
        return { fonte: f, pagina, contagem };
      }),
    );
    const ok = resultados.flatMap((r) => (r.status === 'fulfilled' ? [r.value] : []));
    const falharam = fontes.filter((_, i) => resultados[i].status === 'rejected').map((f) => f.rotulo);

    progresso.current = new Map(ok.map((r) => [r.fonte.origem, { pagina: 1, total: r.pagina.total }]));
    setNotificacoes(ok.flatMap((r) => r.pagina.itens).sort(maisRecentePrimeiro));
    setTotal(ok.reduce((s, r) => s + r.pagina.total, 0));
    setNaoLidas(ok.reduce((s, r) => s + r.contagem, 0));
    // Com uma fonte só não há o que nomear: "seus avisos" já é ela.
    setFontesComFalha(fontes.length > 1 ? falharam : []);
    setFalhouCarga(falharam.length > 0);
    if (ok.length) setCarregado(true);
    if (falharam.length) {
      notify.error(
        'Não foi possível carregar suas notificações. Recarregue a página; se persistir, acione a TI.',
        { toastId: 'sino-notificacoes' },
      );
    }
  }, [fontes]);

  const carregarMais = useCallback(async () => {
    if (carregandoMais) return;
    setCarregandoMais(true);
    try {
      const pendentes = fontes.filter((f) => {
        const p = progresso.current.get(f.origem);
        return p && p.pagina * NOTIFICACOES_POR_PAGINA < p.total;
      });
      const novas = await Promise.all(
        pendentes.map(async (f) => {
          const p = progresso.current.get(f.origem)!;
          const r = await f.pagina(p.pagina + 1, NOTIFICACOES_POR_PAGINA);
          progresso.current.set(f.origem, { pagina: p.pagina + 1, total: r.total });
          return r.itens;
        }),
      );
      setNotificacoes((anteriores) => {
        const vistas = new Set(anteriores.map((n) => n.id));
        return [...anteriores, ...novas.flat().filter((n) => !vistas.has(n.id))].sort(maisRecentePrimeiro);
      });
    } catch (erro) {
      // Ação do usuário: sem aviso, o botão "carregar mais" parecia não ter efeito.
      notify.errorFrom(
        erro,
        'Não foi possível carregar mais notificações. Tente novamente; se persistir, acione a TI.',
      );
    } finally {
      setCarregandoMais(false);
    }
  }, [carregandoMais, fontes]);

  const marcarTodasComoLidas = useCallback(async () => {
    const resultados = await Promise.allSettled(fontes.map((f) => f.marcarTodas()));
    await buscar();
    // at-108: o número do menu acompanha na hora, sem esperar o ciclo de 30s.
    window.dispatchEvent(new CustomEvent('notificacao:lida'));
    const falha = resultados.find((r): r is PromiseRejectedResult => r.status === 'rejected');
    if (falha) {
      // Nunca emitir sucesso para o que falhou (at-60): parte continua não lida, e o badge mostra.
      notify.actionError(falha.reason, { action: 'update', subject: 'o aviso' });
      return;
    }
    notify.success('Todas as notificações foram marcadas como lidas.');
  }, [buscar, fontes]);

  const marcarComoLida = useCallback(
    async (notificacao: UserNotification) => {
      if (notificacao.read) return;
      const fonte = fontes.find((f) => f.origem === (notificacao.origem ?? 'SOAC'));
      if (!fonte) return;
      try {
        await fonte.marcarLida(notificacao);
        await buscar();
        window.dispatchEvent(new CustomEvent('notificacao:lida'));
      } catch (erro) {
        // Sem aviso, a notificação voltava não lida no ciclo seguinte e o clique não dava sinal.
        notify.actionError(erro, { action: 'update', subject: 'o aviso' });
      }
    },
    [buscar, fontes],
  );

  useEffect(() => {
    buscar();
    const intervalo = setInterval(() => {
      if (aberto) buscarContador();
      else buscar();
    }, INTERVALO_MS);
    return () => clearInterval(intervalo);
  }, [buscar, buscarContador, aberto]);

  useEffect(() => {
    const aoCriarNotificacao = () => buscar();
    EVENTOS_DE_AVISO_NOVO.forEach((e) => window.addEventListener(e, aoCriarNotificacao));
    return () => EVENTOS_DE_AVISO_NOVO.forEach((e) => window.removeEventListener(e, aoCriarNotificacao));
  }, [buscar]);

  return {
    notificacoes,
    total,
    naoLidas,
    carregandoMais,
    falhouCarga,
    fontesComFalha,
    carregado,
    carregarMais,
    marcarTodasComoLidas,
    marcarComoLida,
    recarregar: buscar,
  };
}
